import React, { useState } from 'react';
import { STEP_TYPE_ICONS, REVIEW_STATUS_CONFIG } from '../../config/agent.config';
import { StepPreview } from './StepPreview';
import { StepModifier } from './StepModifier';
import { RefusalPanel } from './RefusalPanel';
import type { PlanStep, PlanStatus, StepContent, RefusalReason } from '../../types/agent.types';

interface StepRowProps {
  step: PlanStep;
  planStatus: PlanStatus;
  onApprove: (stepNumber: number) => void;
  onModify: (stepNumber: number, content: StepContent) => void;
  onRefuse: (stepNumber: number, reason: RefusalReason) => void;
  onUndo: (stepNumber: number) => void;
  affaireRoles?: { rv?: string; in?: string; eval1?: string; eval2?: string; eval3?: string };
  affaireSnapshot?: Record<string, unknown>;
}

const CheckSvg = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const XSvg = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const EditSvg = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const StepTypeIconSvg: React.FC<{ type: string }> = ({ type }) => {
  switch (type) {
    case 'email':
      return (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22 6 12 13 2 6" />
        </svg>
      );
    case 'sms':
      return (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="5" y="2" width="14" height="20" rx="2" ry="2" /><line x1="12" y1="18" x2="12.01" y2="18" />
        </svg>
      );
    case 'tracker_com':
      return (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      );
    default:
      return null;
  }
};

export const StepRow: React.FC<StepRowProps> = ({
  step,
  planStatus,
  onApprove,
  onModify,
  onRefuse,
  affaireSnapshot,
  onUndo,
  affaireRoles,
}) => {
  const [showPreview, setShowPreview] = useState(true);
  const [showModifier, setShowModifier] = useState(false);
  const [showRefusal, setShowRefusal] = useState(false);

  const isPending = planStatus === 'pending';
  const reviewStatus = step.review_status || 'pending';
  const isStepPending = reviewStatus === 'pending';
  const reviewConfig = REVIEW_STATUS_CONFIG[reviewStatus] || REVIEW_STATUS_CONFIG.pending;
  const stepTypeIcon = STEP_TYPE_ICONS[step.type];

  const rowClassName = `agent-step-row${reviewStatus !== 'pending' ? ` agent-step-row--${reviewStatus}` : ''}`;
  const titleClassName = `agent-step-title${reviewStatus === 'refused' ? ' agent-step-title--refused' : ''}`;

  const details = (step.execution_result?.details || '').toLowerCase();
  const isSkipped = reviewStatus === 'refused' || details.includes('refusé') || details.includes('refusée') || details.includes('skippé') || details.includes('ignorée') || details.includes('non exécuté');
  const hasExecutedSuccessfully = step.execution_result && step.execution_result.success && !isSkipped;

  const getMarkerClass = () => {
    if (reviewStatus === 'approved' || (reviewStatus === 'pending' && hasExecutedSuccessfully)) return 'agent-step-marker agent-step-marker--approved';
    if (reviewStatus === 'modified') return 'agent-step-marker agent-step-marker--modified';
    if (isSkipped) return 'agent-step-marker agent-step-marker--refused';
    return 'agent-step-marker agent-step-marker--pending';
  };

  const renderMarkerContent = () => {
    if (reviewStatus === 'approved' || (reviewStatus === 'pending' && hasExecutedSuccessfully)) return <CheckSvg />;
    if (reviewStatus === 'modified') return <EditSvg />;
    if (isSkipped) return <XSvg />;
    return step.step_number;
  };

  return (
    <div className={rowClassName}>
      <div className="agent-step-main">
        <span className={getMarkerClass()}>
          {renderMarkerContent()}
        </span>

        <div className="agent-step-body">
          <div className="agent-step-title-row">
            <span className="agent-step-type-icon">
              {stepTypeIcon && <StepTypeIconSvg type={stepTypeIcon} />}
            </span>
            <span className={titleClassName}>
              {step.title}
              {reviewStatus === 'modified' && ' (modifie)'}
            </span>

            {!isStepPending && (
              <>
                <span className={`agent-step-review-badge ${reviewConfig.badgeClass}`}>
                  {reviewConfig.label}
                </span>
                {isPending && (
                  <button
                    className="agent-step-undo"
                    onClick={() => onUndo(step.step_number)}
                    title="Annuler"
                  >
                    &#8617;
                  </button>
                )}
              </>
            )}

            {(isPending || planStatus === 'validated' || planStatus === 'executing') && !showModifier && !showRefusal && (
              <button
                className="agent-step-preview-toggle"
                onClick={() => setShowPreview(!showPreview)}
              >
                {showPreview ? 'Masquer' : 'Voir apercu'}
              </button>
            )}
          </div>

          {step.execution_result && (
            <div className="agent-step-execution-result">
              {step.execution_result.success
                ? step.execution_result.details
                : step.execution_result.details || step.execution_result.error || 'Erreur inconnue'}
            </div>
          )}

          {reviewStatus === 'refused' && step.refusal_reason && (
            <div className="agent-step-refusal-reason">
              Raison: "{step.refusal_reason.comment || REVIEW_STATUS_CONFIG.refused.label}"
            </div>
          )}

          {reviewStatus === 'modified' && step.modified_content && (
            <div className="agent-step-modification-summary">
              Contenu modifie par l'utilisateur
            </div>
          )}

          {showPreview && !showModifier && !showRefusal && (
            <StepPreview
              step={step}
              affaireRoles={affaireRoles}
              affaireSnapshot={affaireSnapshot}
              onAssignChange={isPending && (isStepPending || reviewStatus === 'modified') && step.type === 'tracker_com' ? (email: string) => {
                const currentContent = step.modified_content || step.content || {};
                onModify(step.step_number, { ...currentContent, assign_to: email } as any);
              } : undefined}
            />
          )}

          {showModifier && (
            <StepModifier
              step={step}
              affaireRoles={affaireRoles}
              affaireSnapshot={affaireSnapshot}
              onSave={(modified) => {
                onModify(step.step_number, modified);
                setShowModifier(false);
              }}
              onCancel={() => setShowModifier(false)}
            />
          )}

          {showRefusal && (
            <RefusalPanel
              onConfirm={(reason) => {
                onRefuse(step.step_number, reason);
                setShowRefusal(false);
              }}
              onCancel={() => setShowRefusal(false)}
            />
          )}
        </div>
      </div>

      {isPending && isStepPending && !showModifier && !showRefusal && (
        <div className="agent-step-actions">
          <button
            className="agent-step-btn agent-step-btn--approve"
            onClick={() => onApprove(step.step_number)}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Approuver
          </button>
          <button
            className="agent-step-btn agent-step-btn--modify"
            onClick={() => {
              setShowModifier(true);
              setShowPreview(false);
              setShowRefusal(false);
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            Modifier
          </button>
          <button
            className="agent-step-btn agent-step-btn--refuse"
            onClick={() => {
              setShowRefusal(true);
              setShowPreview(false);
              setShowModifier(false);
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
            Refuser
          </button>
        </div>
      )}
    </div>
  );
};
